import { clsx } from 'clsx'
import type { SubscriptionTier } from '@/features/home/queries'
import styles from './PlansGrid.module.css'

type Feature = { text: string; muted?: boolean }
type Plan = {
  tier:     SubscriptionTier
  eyebrow:  string
  name:     string
  price:    string
  per:      string
  desc:     string
  features: Feature[]
  featured?: boolean
  money?:   boolean
}

const PLANS: Plan[] = [
  {
    tier: 'FREE', eyebrow: 'Gratuito', name: 'Banco\nde reservas', price: '0', per: '/sempre',
    desc: 'Tudo pra jogar com os amigos — uma liga privada, escalação e placar ao vivo.',
    features: [
      { text: '1 liga privada' },
      { text: 'Até 20 participantes' },
      { text: 'Pontuação padrão' },
      { text: 'Placar ao vivo' },
      { text: 'Ligas Head-to-Head', muted: true },
      { text: 'Ligas pagas com Pix', muted: true },
    ],
  },
  {
    tier: 'ORGANIZADOR', eyebrow: 'Organizador', name: 'XI\nTitular', price: '19', per: '/mês',
    desc: 'Pra quem administra várias ligas e quer ferramentas de quem leva a sério.',
    featured: true,
    features: [
      { text: 'Até 5 ligas privadas' },
      { text: 'Até 100 participantes' },
      { text: 'Ligas Head-to-Head' },
      { text: 'Analytics avançado' },
      { text: 'Feed de eventos & banter' },
      { text: 'Ligas pagas com Pix', muted: true },
    ],
  },
  {
    tier: 'LIGA_PRO', eyebrow: 'Liga Pro', name: 'Capitão\nda seleção', price: '49', per: '/mês',
    desc: 'Pra power-users — ligas com premiação real e recursos avançados.',
    money: true,
    features: [
      { text: 'Tudo do Organizador' },
      { text: 'Ligas pagas com prize pool' },
      { text: 'Recebimento via Pix' },
      { text: 'Regras de pontuação custom' },
      { text: 'Export CSV / acesso à API' },
      { text: 'Beta de novos esportes (CS2)' },
    ],
  },
]

/** Static plans grid mirroring the landing page's "04 / PLANOS" pricing style. */
export function PlansGrid({ currentTier }: { currentTier: SubscriptionTier | undefined }) {
  return (
    <div className={styles.pricing}>
      {PLANS.map((plan) => {
        const isCurrent = currentTier === plan.tier
        return (
          <div
            key={plan.tier}
            className={clsx(styles.card, plan.featured && styles.featured, plan.money && styles.money)}
          >
            <div className={styles.tier}>{plan.eyebrow}</div>
            <div className={styles.name}>
              {plan.name.split('\n').map((line, i) => (
                <span key={i}>{line}{i === 0 ? <br /> : null}</span>
              ))}
            </div>
            <div className={styles.tag}>
              <span className={styles.curr}>R$</span>{plan.price}<span className={styles.per}>{plan.per}</span>
            </div>
            {plan.money && <span className={styles.moneyBadge}>🏆 Ligas pagas</span>}
            <p className={styles.desc}>{plan.desc}</p>
            <ul className={styles.list}>
              {plan.features.map((f) => (
                <li key={f.text} className={clsx(f.muted && styles.muted)}>
                  <span className={styles.check} aria-hidden="true">{f.muted ? '—' : '✓'}</span>
                  {f.text}
                </li>
              ))}
            </ul>
            <PlanCta plan={plan} isCurrent={isCurrent} />
          </div>
        )
      })}
    </div>
  )
}

function PlanCta({ plan, isCurrent }: { plan: Plan; isCurrent: boolean }) {
  if (isCurrent) {
    return <button type="button" className={clsx(styles.cta, styles.current)} disabled>Plano atual</button>
  }
  if (plan.tier === 'FREE') {
    return <button type="button" className={clsx(styles.cta, styles.current)} disabled>Plano após o beta</button>
  }
  // Payments are deferred (post-MVP) — the CTA is present but not yet wired.
  return (
    <button
      type="button"
      className={clsx(styles.cta, plan.money ? styles.moneyCta : styles.solid)}
      title="Pagamentos via Pix chegam em breve"
    >
      Assinar
    </button>
  )
}
